/**
 * Signup Page
 *
 * User registration with email/password and OAuth options.
 */

import {
  Alert,
  AlertDescription,
  AuthTemplate,
  Button,
  DividerWithText,
  Form,
  GitHubIcon,
  GoogleIcon,
  Grid,
  Input,
  Label,
  Stack,
  Text,
} from '@cortex/ui';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

import { supabase } from '@/lib/supabase';

const searchSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute('/signup')({
  validateSearch: searchSchema,
  component: SignupPage,
});

function SignupPage() {
  const navigate = useNavigate();
  const { redirect } = Route.useSearch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        setError(error.message);
        return;
      }

      // Show success message - user needs to verify email
      navigate({
        to: '/login',
        search: { message: 'Check your email to verify your account' },
      });
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthSignup = async (provider: 'github' | 'google') => {
    setError(null);
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback?redirect=${encodeURIComponent(redirect ?? '/')}`,
        },
      });

      if (error) {
        setError(error.message);
        setIsLoading(false);
      }
    } catch {
      setError('An unexpected error occurred');
      setIsLoading(false);
    }
  };

  return (
    <AuthTemplate title="Create your account" subtitle="">
      <Text color="secondary" align="center" className="!-mt-4">
        Already have an account?{' '}
        <Link
          to="/login"
          search={{ redirect }}
          className="text-primary hover:underline"
        >
          Sign in
        </Link>
      </Text>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Form onSubmit={handleSubmit} className="mt-8" gap="lg">
        <Stack gap="md">
          <Stack gap="xs">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
            />
          </Stack>

          <Stack gap="xs">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              placeholder="••••••••"
            />
          </Stack>

          <Stack gap="xs">
            <Label htmlFor="confirmPassword">Confirm password</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              placeholder="••••••••"
            />
          </Stack>
        </Stack>

        <Button type="submit" loading={isLoading} className="w-full">
          Create account
        </Button>
      </Form>

      <DividerWithText text="Or continue with" />

      <Grid columns={2} gap="md">
        <Button
          type="button"
          variant="outline"
          onClick={() => handleOAuthSignup('github')}
          disabled={isLoading}
          className="gap-2"
        >
          <GitHubIcon className="h-5 w-5" />
          GitHub
        </Button>

        <Button
          type="button"
          variant="outline"
          onClick={() => handleOAuthSignup('google')}
          disabled={isLoading}
          className="gap-2"
        >
          <GoogleIcon className="h-5 w-5" />
          Google
        </Button>
      </Grid>

      <Text color="secondary" align="center" size="xs">
        By signing up, you agree to our{' '}
        <Button variant="link" asChild className="h-auto p-0 text-xs">
          <a href="/terms">Terms of Service</a>
        </Button>{' '}
        and{' '}
        <Button variant="link" asChild className="h-auto p-0 text-xs">
          <a href="/privacy">Privacy Policy</a>
        </Button>
      </Text>
    </AuthTemplate>
  );
}
